import { useQuery, useQueryClient } from '@tanstack/react-query';

import type { App } from '@keboola/api-client/developerPortal/types';

import { api } from '../../api';
import { fetchAllPages } from '../../paging';

/** Query keys, exported so vendor mutations can invalidate the public catalog and detail pages. */
export const appQueryKeys = {
  published: ['apps', 'published'] as const,
  publishedDetail: (appId: string) => ['apps', 'published', appId] as const,
} as const;

/**
 * Every published component.
 *
 * - The whole list: `GET /apps` takes only `offset`/`limit`, so all filtering runs client-side.
 * - {@link usePublishedApp} serves the detail page from this cache.
 */
export const usePublishedApps = () =>
  useQuery({
    queryKey: appQueryKeys.published,
    queryFn: ({ signal }): Promise<App[]> =>
      fetchAllPages((range) => api.apps.getPublishedApps(range, signal), 'the component catalog'),
  });

/**
 * One published component, seeded from the cached catalog list.
 *
 * - A `GET /apps` row is identical to the `GET /apps/{app}` response.
 * - `initialDataUpdatedAt` carries the list's age, so `staleTime` decides as it would for the list.
 */
export const usePublishedApp = (appId: string) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: appQueryKeys.publishedDetail(appId),
    queryFn: ({ signal }) => api.apps.getPublishedApp(appId, signal),
    initialData: () =>
      queryClient.getQueryData<App[]>(appQueryKeys.published)?.find((app) => app.id === appId),
    initialDataUpdatedAt: () => queryClient.getQueryState(appQueryKeys.published)?.dataUpdatedAt,
  });
};
